import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter } from '../../components/_WMS/FilterComponent'
import { SettingsSearch } from '../../components/Settings/SettingsSearch'
import { TypesSetting } from '../../components/Settings/TypesSetting'
import axios, { AxiosResponse } from 'axios'
import { ApiConfig } from '../../config/api'
import { useApi } from '../../hooks/useApi'
import { Status } from '../../hooks/useFetchState'
import { Box, Button, CircularProgress, Fade, Typography } from '@mui/material'
import { Loading } from '../../components/UI'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { FilterExtraAction } from '../../components/_WMS/FilterComponent/types'
import RestoreIcon from '@mui/icons-material/Restore'
import SaveIcon from '@mui/icons-material/Save'
import CustomAlert from '../../components/_WMS/AlertMessage/AlertMessage'
import { Modal } from '../../components/_EOS/Modal'
import { useTypesSetting } from '../../hooks/useTypesSetting'
import { eTypeSetting } from '../../enums/eTypeSetting'
import { eSetting } from '../../enums/eSetting'
import { eTypeStorage } from '../../enums/eTypeStorage'
import { useProductOrigins } from '../../hooks/useProductOrigins'

type PendingChange = {
  value: string
  productOriginId: number | null
}

export const SettingsPage = () => {
  const history = useHistory()
  const { callApi: callApiSettingsReset, state: apiSettingsStateReset } =
    useApi()
  const { callApi: callApiSaveSettings } = useApi()
  const {
    typeSettingState: { typeSetting: typeSettings, error: typeSettingsError },
    getTypesSetting
  } = useTypesSetting()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const { isActive, message, isSuccess, openSnackBar } = useSnackbar()
  const [resetKey, setResetKey] = useState(0)

  // Cambios pendientes: settingsValuesId → { value, productOriginId }
  const [pendingChanges, setPendingChanges] = useState<
    Record<number, PendingChange>
  >({})
  const [isSaving, setIsSaving] = useState(false)
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false)
  const [pendingClearFilters, setPendingClearFilters] = useState(false)
  // Ref: settingsMetadataId → {settingsValuesId, productOriginId, typeSettingId}
  const loadedSettingsMapRef = useRef<
    Record<
      number,
      {
        settingsValuesId: number
        productOriginId: number | null
        typeSettingId: number
        name: string
        value: string
      }
    >
  >({})
  // Ref: settingsValuesId → typeSettingId (para calcular hasPendingChanges por accordion)
  const valuesIdToTypeSettingIdRef = useRef<Record<number, number>>({})
  // Ref: datos de los dos settings en conflicto potencial (17 y 20)
  const mutualExclusionDataRef = useRef<
    Record<
      number,
      {
        settingsValuesId: number
        productOriginId: number | null
        value: string
        name: string
      }
    >
  >({})
  const [conflictModalOpen, setConflictModalOpen] = useState(false)

  const handleSettingsLoaded = (
    settings: {
      settingsMetadataId: number
      settingsValuesId: number
      productOriginId: number | null
      typeSettingId: number
      name: string
      value: string
    }[]
  ) => {
    settings.forEach((s) => {
      loadedSettingsMapRef.current[s.settingsMetadataId] = {
        settingsValuesId: s.settingsValuesId,
        productOriginId: s.productOriginId,
        typeSettingId: s.typeSettingId,
        name: s.name,
        value: s.value
      }
      valuesIdToTypeSettingIdRef.current[s.settingsValuesId] = s.typeSettingId
      if (
        s.settingsMetadataId === eSetting.AllowMultipleOrdersPerWorkOrder ||
        s.settingsMetadataId ===
          eSetting.PrioritizeByBrandAndCourierForExternalStorage
      ) {
        mutualExclusionDataRef.current[s.settingsMetadataId] = {
          settingsValuesId: s.settingsValuesId,
          productOriginId: s.productOriginId,
          value: s.value,
          name: s.name
        }
      }
    })
  }
  const [blockedNavigation, setBlockedNavigation] = useState<any>(null)
  const unblockRef = useRef<(() => void) | null>(null)

  const hasUnsavedChanges = Object.keys(pendingChanges).length > 0
  const pendingCount = Object.keys(pendingChanges).length

  useEffect(() => {
    if (typeSettings === undefined) {
      getTypesSetting()
    }
  }, [])

  // Bloquea la navegación SPA cuando hay cambios sin guardar
  useEffect(() => {
    if (hasUnsavedChanges) {
      const unblock = history.block((location: any) => {
        setBlockedNavigation(location)
        setUnsavedModalOpen(true)
        return false
      })
      unblockRef.current = unblock
      return () => {
        unblock()
        unblockRef.current = null
      }
    } else {
      unblockRef.current?.()
      unblockRef.current = null
    }
  }, [hasUnsavedChanges])

  // Bloquea el cierre de pestaña / recarga del navegador
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedChanges])

  const brandId =
    selectedFilters.find((f) => f.key === 'productOriginId')?.value ?? null

  const brand =
    selectedFilters.find((f) => f.key === 'productOriginId')?.displayValue ??
    null

  const {
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const selectedBrandTypeStorageId = productOrigins?.find(
    (po) => String(po.productOriginId) === brandId
  )?.typeStorageId

  const generalSettings =
    typeSettings?.filter((t) => t.typeSettingId === eTypeSetting.General) ?? []

  const brandedSettings =
    typeSettings?.filter((t) => t.typeSettingId !== eTypeSetting.General) ?? []

  const typeSettingHasPendingChanges = (typeSettingId: number): boolean =>
    Object.keys(pendingChanges).some(
      (vid) => valuesIdToTypeSettingIdRef.current[Number(vid)] === typeSettingId
    )

  const pendingValuesByValuesId = Object.fromEntries(
    Object.entries(pendingChanges).map(([id, change]) => [
      Number(id),
      change.value
    ])
  )

  const handleSettingsCollapse = () => {
    // Los datos se mantienen al contraer — no se recarga al volver a expandir
  }

  const AUTOMATIZACIONES_SECTION_GENERAL = {
    label: 'General',
    metadataIds: [
      eSetting.WorkOrderInheritsOrderTag,
      eSetting.AllowMultipleOrdersPerWorkOrder
    ]
  }
  const AUTOMATIZACIONES_SECTION_INTERNO = {
    label: 'Aplica almacenamiento interno',
    metadataIds: [eSetting.PrioritizeWorkOrdersForInternalStorage]
  }
  const AUTOMATIZACIONES_SECTION_EXTERNO = {
    label: 'Aplica almacenamiento externo',
    metadataIds: [
      eSetting.AutoCreatePickingRouteForExternalStorage,
      eSetting.PrioritizeByBrandAndCourierForExternalStorage
    ]
  }

  const ALL_AUTOMATIZACIONES_SECTIONS = [
    AUTOMATIZACIONES_SECTION_GENERAL,
    AUTOMATIZACIONES_SECTION_INTERNO,
    AUTOMATIZACIONES_SECTION_EXTERNO
  ]

  const REGLAS_RUTA_PICKING_SECTION_RECALL = {
    label: 'Aplica para Rutas Recall',
    metadataIds: [eSetting.RecallMaxQtyItemOnPicking]
  }

  const getAutomatizacionesSections = (typeStorageId?: number) => {
    const sections = [AUTOMATIZACIONES_SECTION_GENERAL]
    if (
      !typeStorageId ||
      typeStorageId === eTypeStorage.Interno ||
      typeStorageId === eTypeStorage.Mixto
    ) {
      sections.push(AUTOMATIZACIONES_SECTION_INTERNO)
    }
    if (
      !typeStorageId ||
      typeStorageId === eTypeStorage.Externo ||
      typeStorageId === eTypeStorage.Mixto
    ) {
      sections.push(AUTOMATIZACIONES_SECTION_EXTERNO)
    }
    return sections
  }

  const automatizacionesSections = getAutomatizacionesSections(
    selectedBrandTypeStorageId
  )

  const excludedAutomatizacionesMetadataIds = ALL_AUTOMATIZACIONES_SECTIONS.filter(
    (section) => !automatizacionesSections.includes(section)
  ).flatMap((section) => section.metadataIds)

  // Detecta si los dos settings mutuamente excluyentes están ambos activos
  const isSettingActive = (metaId: number) => {
    const data = mutualExclusionDataRef.current[metaId]
    if (!data) return false
    return (
      (pendingChanges[data.settingsValuesId]?.value ?? data.value) === 'true'
    )
  }

  // El conflicto solo aplica para marcas con almacenamiento externo
  const isExternalBrand = selectedBrandTypeStorageId === eTypeStorage.Externo

  const hasConflict =
    isExternalBrand &&
    isSettingActive(eSetting.AllowMultipleOrdersPerWorkOrder) &&
    isSettingActive(eSetting.PrioritizeByBrandAndCourierForExternalStorage)

  const conflictingMetaIds: Set<number> = hasConflict
    ? new Set([
        eSetting.AllowMultipleOrdersPerWorkOrder,
        eSetting.PrioritizeByBrandAndCourierForExternalStorage
      ])
    : new Set()

  // Contadores separados: pendientes sin conflicto vs. en conflicto
  const conflictValuesIds = new Set<number>(
    [
      mutualExclusionDataRef.current[eSetting.AllowMultipleOrdersPerWorkOrder]
        ?.settingsValuesId,
      mutualExclusionDataRef.current[
        eSetting.PrioritizeByBrandAndCourierForExternalStorage
      ]?.settingsValuesId
    ].filter((v): v is number => v !== undefined && hasConflict)
  )

  const nonConflictPendingCount = Object.keys(pendingChanges).filter(
    (vid) => !conflictValuesIds.has(Number(vid))
  ).length

  const handleSettingChange = (
    settingsValuesId: number,
    newValue: string,
    productOriginId: number | null,
    isDirty: boolean
  ) => {
    setPendingChanges((prev) => {
      if (!isDirty) {
        const { [settingsValuesId]: _, ...rest } = prev
        return rest
      }
      return {
        ...prev,
        [settingsValuesId]: { value: newValue, productOriginId }
      }
    })
    // Abrir modal de advertencia si el cambio genera un conflicto
    if (newValue === 'true' && isDirty) {
      const isAllow =
        settingsValuesId ===
        mutualExclusionDataRef.current[eSetting.AllowMultipleOrdersPerWorkOrder]
          ?.settingsValuesId
      const isPrioritize =
        settingsValuesId ===
        mutualExclusionDataRef.current[
          eSetting.PrioritizeByBrandAndCourierForExternalStorage
        ]?.settingsValuesId
      if (isAllow || isPrioritize) {
        const otherMetaId = isAllow
          ? eSetting.PrioritizeByBrandAndCourierForExternalStorage
          : eSetting.AllowMultipleOrdersPerWorkOrder
        if (isExternalBrand && isSettingActive(otherMetaId)) {
          setConflictModalOpen(true)
        }
      }
    }
  }

  const saveAllChanges = async () => {
    if (!hasUnsavedChanges) return
    setIsSaving(true)

    try {
      // Agrupa los cambios por productOriginId para una sola llamada por grupo
      const groups = new Map<
        number | null,
        Array<{ settingsValuesId: number; value: string }>
      >()

      Object.entries(pendingChanges).forEach(([id, change]) => {
        const existing = groups.get(change.productOriginId) ?? []
        groups.set(change.productOriginId, [
          ...existing,
          { settingsValuesId: Number(id), value: change.value }
        ])
      })

      await callApiSaveSettings(
        Promise.all(
          Array.from(groups.entries()).map(([originId, values]) => {
            const url = new URL(`${ApiConfig.SettiingsApi}/newValues`)
            if (originId !== null) {
              url.searchParams.set('productOriginId', String(originId))
            }
            return axios.patch(url.toString(), values)
          })
        ) as unknown as Promise<AxiosResponse<any>>
      )

      resetSettingsState()
      openSnackBar('Configuraciones guardadas correctamente.', true)

      // Si la acción de guardar fue gatillada por la advertencia de navegación, continuar
      if (blockedNavigation) {
        unblockRef.current?.()
        unblockRef.current = null
        const target = blockedNavigation
        setBlockedNavigation(null)
        setUnsavedModalOpen(false)
        history.push(target.pathname)
      }
    } catch (err) {
      openSnackBar(`Error al guardar las configuraciones: ${err}`, false)
    } finally {
      setIsSaving(false)
    }
  }

  const resetSettingsState = () => {
    setPendingChanges({})
    valuesIdToTypeSettingIdRef.current = {}
    mutualExclusionDataRef.current = {}
    setConflictModalOpen(false)
    setResetKey((k) => k + 1)
  }

  const executeClearFilters = () => {
    setSelectedFilters([])
    resetSettingsState()
  }

  const handleSetSelectedFilters = (filtersOrUpdater: any) => {
    const newFilters =
      typeof filtersOrUpdater === 'function'
        ? filtersOrUpdater(selectedFilters)
        : filtersOrUpdater

    if (newFilters.length === 0 && hasUnsavedChanges) {
      setPendingClearFilters(true)
      setUnsavedModalOpen(true)
      return
    }

    if (newFilters.length === 0) {
      executeClearFilters()
    } else {
      setSelectedFilters(newFilters)
    }
  }

  const handleConfirmNavigation = () => {
    unblockRef.current?.()
    unblockRef.current = null
    setPendingChanges({})
    setUnsavedModalOpen(false)

    if (pendingClearFilters) {
      setPendingClearFilters(false)
      executeClearFilters()
      return
    }

    const target = blockedNavigation
    setBlockedNavigation(null)
    if (target) {
      history.push(target.pathname)
    }
  }

  const patchSettingsReset = (id: number) => {
    const url = new URL(`${ApiConfig.SettiingsApi}/brand/${id}/reset`)
    return axios.patch(url.toString())
  }

  const patchApiSettingsReset = async () => {
    callApiSettingsReset(patchSettingsReset(brandId))
      .then(() => {
        setResetModalOpen(false)
        resetSettingsState()
        openSnackBar(
          'Todos los parámetros de configuración han sido reestablecidos correctamente.',
          true
        )
      })
      .catch((err) => {
        setResetModalOpen(false)
        openSnackBar(
          `Error al intentar reestablecer los parámetros de configuración [${err.message}]`,
          false
        )
      })
  }

  const extraActions: FilterExtraAction[] = [
    {
      label: 'Reestablecer parámetros',
      onClick: () => setResetModalOpen(true),
      icon: <RestoreIcon />
    }
  ]

  return (
    <>
      <StandardPage pageTitle=''>
        <SettingsSearch
          onSearchClick={handleSetSelectedFilters}
          selectedFilters={selectedFilters}
          setSelectedFilters={handleSetSelectedFilters}
          extraActions={selectedFilters.length > 0 ? extraActions : []}
        />

        {/* Barra de guardado: visible solo cuando hay cambios pendientes */}
        {hasUnsavedChanges && (
          <Fade in={hasUnsavedChanges} timeout={300} mountOnEnter unmountOnExit>
            <Box display='flex' alignItems='center' gap={2} mb={2}>
              <Box flex={1}>
                {nonConflictPendingCount > 0 && (
                  <CustomAlert
                    severity='warning'
                    message={
                      nonConflictPendingCount === 1
                        ? '1 configuración con cambios sin guardar'
                        : `${nonConflictPendingCount} configuraciones con cambios sin guardar`
                    }
                  />
                )}
                {hasConflict && (
                  <CustomAlert
                    severity='error'
                    message={
                      <Box>
                        <Typography variant='body2' fontWeight={600}>
                          2 configuraciones en conflicto:
                        </Typography>
                        <Typography variant='body2'>
                          <b>
                            {
                              mutualExclusionDataRef.current[
                                eSetting.AllowMultipleOrdersPerWorkOrder
                              ]?.name
                            }
                          </b>
                          {' y '}
                          <b>
                            {
                              mutualExclusionDataRef.current[
                                eSetting
                                  .PrioritizeByBrandAndCourierForExternalStorage
                              ]?.name
                            }
                          </b>
                          {' no pueden estar activas al mismo tiempo.'}
                        </Typography>
                      </Box>
                    }
                  />
                )}
              </Box>
              <Button
                variant='contained'
                size='medium'
                onClick={saveAllChanges}
                disabled={isSaving || hasConflict}
                startIcon={
                  isSaving ? (
                    <CircularProgress size={14} color='inherit' />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {isSaving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </Box>
          </Fade>
        )}

        {apiSettingsStateReset.status === Status.Loading && (
          <Typography>
            <Loading />
          </Typography>
        )}

        {/* Loading inicial mientras se cargan los tipos de configuración */}
        {typeSettings === undefined && !typeSettingsError ? (
          <Loading />
        ) : (
          typeSettings === undefined &&
          typeSettingsError && (
            <Box textAlign='center' py={4}>
              <Typography color='error' gutterBottom>
                No fue posible cargar las configuraciones. {typeSettingsError}
              </Typography>
              <Button variant='outlined' onClick={getTypesSetting}>
                Reintentar
              </Button>
            </Box>
          )
        )}

        {/* Configuraciones generales: solo visibles en la vista general (sin marca) */}
        {selectedFilters.length === 0 &&
          generalSettings.map((type) => (
            <TypesSetting
              key={`${type.typeSettingId}-${resetKey}`}
              typeSettingId={type.typeSettingId}
              name={type.name}
              description={type.description}
              brandId={null}
              pendingValuesByValuesId={pendingValuesByValuesId}
              hasPendingChanges={typeSettingHasPendingChanges(
                type.typeSettingId
              )}
              sections={
                type.typeSettingId === eTypeSetting.Automatizaciones
                  ? automatizacionesSections
                  : type.typeSettingId === eTypeSetting.ReglasRutaPicking
                    ? [REGLAS_RUTA_PICKING_SECTION_RECALL]
                    : undefined
              }
              hiddenMetadataIds={
                type.typeSettingId === eTypeSetting.Automatizaciones
                  ? excludedAutomatizacionesMetadataIds
                  : undefined
              }
              onSettingChange={handleSettingChange}
              onCollapse={handleSettingsCollapse}
              onSettingsLoaded={handleSettingsLoaded}
            />
          ))}

        {/* Configuraciones por marca: solo visibles cuando hay marca seleccionada */}
        {selectedFilters.length > 0 &&
          brandedSettings.map((type) => (
            <TypesSetting
              key={`${type.typeSettingId}-${resetKey}`}
              typeSettingId={type.typeSettingId}
              name={type.name}
              description={type.description}
              brandId={brandId}
              pendingValuesByValuesId={pendingValuesByValuesId}
              hasPendingChanges={typeSettingHasPendingChanges(
                type.typeSettingId
              )}
              conflictingMetaIds={
                type.typeSettingId === eTypeSetting.Automatizaciones
                  ? conflictingMetaIds
                  : undefined
              }
              sections={
                type.typeSettingId === eTypeSetting.Automatizaciones
                  ? automatizacionesSections
                  : type.typeSettingId === eTypeSetting.ReglasRutaPicking
                    ? [REGLAS_RUTA_PICKING_SECTION_RECALL]
                    : undefined
              }
              hiddenMetadataIds={
                type.typeSettingId === eTypeSetting.Automatizaciones
                  ? excludedAutomatizacionesMetadataIds
                  : undefined
              }
              onSettingChange={handleSettingChange}
              onCollapse={handleSettingsCollapse}
              onSettingsLoaded={handleSettingsLoaded}
            />
          ))}
      </StandardPage>

      {/* Modal de confirmación para reestablecer */}
      <Modal
        open={resetModalOpen}
        title='Reestablecer configuración'
        loading={apiSettingsStateReset.status === Status.Loading}
        onClose={() => setResetModalOpen(false)}
        cancelButton={{
          title: 'Cancelar',
          action: () => setResetModalOpen(false),
          disabled: apiSettingsStateReset.status === Status.Loading
        }}
        successButton={{
          title: 'Confirmar',
          action: patchApiSettingsReset,
          disabled: apiSettingsStateReset.status === Status.Loading
        }}
      >
        <Typography variant='body1'>
          ¿Estás seguro de que deseas reestablecer todos los parámetros de
          configuración para la marca: <b>{brand}</b>?
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Esta acción no se puede deshacer.
        </Typography>
      </Modal>

      {/* Modal de advertencia por configuraciones en conflicto */}
      <Modal
        open={conflictModalOpen}
        title='Configuraciones en conflicto'
        onClose={() => setConflictModalOpen(false)}
        successButton={{
          title: 'Entendido',
          action: () => setConflictModalOpen(false)
        }}
      >
        <Typography variant='body1'>
          Las configuraciones{' '}
          <b>
            '
            {
              mutualExclusionDataRef.current[
                eSetting.AllowMultipleOrdersPerWorkOrder
              ]?.name
            }
            '
          </b>{' '}
          y{' '}
          <b>
            '
            {
              mutualExclusionDataRef.current[
                eSetting.PrioritizeByBrandAndCourierForExternalStorage
              ]?.name
            }
            '
          </b>{' '}
          no pueden estar activas al mismo tiempo.
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Desactiva una de las dos configuraciones antes de guardar los cambios.
        </Typography>
      </Modal>

      {/* Modal de advertencia al intentar salir con cambios sin guardar */}
      <Modal
        open={unsavedModalOpen}
        title='Cambios sin guardar'
        loading={isSaving}
        onClose={() => setUnsavedModalOpen(false)}
        cancelButton={{
          title: 'Cancelar',
          action: () => {
            setUnsavedModalOpen(false)
            setPendingClearFilters(false)
          },
          disabled: isSaving
        }}
        successButton={{
          title: 'Salir sin guardar',
          action: handleConfirmNavigation,
          disabled: isSaving
        }}
      >
        <Typography variant='body1'>
          Tienes{' '}
          <b>
            {pendingCount}{' '}
            {pendingCount === 1
              ? 'configuración con cambios'
              : 'configuraciones con cambios'}
          </b>{' '}
          que no han sido guardados.
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Si sales ahora, se perderán todos los cambios realizados. ¿Deseas
          continuar?
        </Typography>
      </Modal>

      <SnackBar
        open={isActive}
        message={message}
        status={isSuccess ? 'success' : 'error'}
      />
    </>
  )
}
